/**
 * PDF解析関連の型定義
 */

/**
 * ドキュメントタイプ
 * - auto: 自動検出（AIがドキュメントの種類を判定）
 * - pedigree: 血統書
 * - microchip: マイクロチップ証明書
 * - geneticTest: 遺伝子検査結果
 */
export type DocumentType = 'auto' | 'pedigree' | 'microchip' | 'geneticTest';

/**
 * 具体的なドキュメントタイプ（auto以外）
 * 解析結果では常にこの型が使用される
 */
export type SpecificDocumentType = Exclude<DocumentType, 'auto'>;

/**
 * 遺伝子検査結果の判定
 */
export type GeneticTestResultType = 'clear' | 'carrier' | 'affected' | 'unknown';

/**
 * 抽出された血統書データ
 */
export interface ExtractedPedigreeData {
  /** 正式名称（血統書名） */
  officialName?: string;
  /** 犬種 */
  breed?: string;
  /** JKC登録番号 */
  jkcRegistrationNumber?: string;
  /** DNA登録番号 */
  dnaRegistrationNumber?: string;
  /** 性別 */
  gender?: 'male' | 'female';
  /** 生年月日（YYYY-MM-DD形式） */
  birthDate?: string;
  /** 毛色 */
  color?: string;
  /** 繁殖者名 */
  breederName?: string;
  /** 所有者名 */
  ownerName?: string;
  /** 血統情報（父母・祖父母） */
  ancestors?: {
    father?: {
      name?: string;
      registrationNumber?: string;
    };
    mother?: {
      name?: string;
      registrationNumber?: string;
    };
    paternalGrandfather?: {
      name?: string;
      registrationNumber?: string;
    };
    paternalGrandmother?: {
      name?: string;
      registrationNumber?: string;
    };
    maternalGrandfather?: {
      name?: string;
      registrationNumber?: string;
    };
    maternalGrandmother?: {
      name?: string;
      registrationNumber?: string;
    };
  };
}

/**
 * 抽出されたマイクロチップデータ
 */
export interface ExtractedMicrochipData {
  /** マイクロチップ番号 */
  microchipNumber: string;
  /** 登録日（YYYY-MM-DD形式） */
  registrationDate?: string;
  /** 暗証記号（PIN） */
  pin?: string;
}

/**
 * 抽出された遺伝子検査結果データ
 */
export interface ExtractedGeneticTestData {
  /** どうぶつ名（犬の名前） */
  animalName?: string;
  /** 検査結果リスト */
  testResults: Array<{
    /** 検査項目名 */
    testName: string;
    /** 検査結果 */
    result: GeneticTestResultType;
  }>;
  /** 検査日（YYYY-MM-DD形式） */
  testDate?: string;
  /** 検査機関 */
  laboratory?: string;
}

/**
 * 統合抽出データ（auto検出時に複数ドキュメントをマージ）
 * 血統書データを基盤に、マイクロチップ・遺伝子検査データを追加
 */
export interface ExtractedUnifiedData extends ExtractedPedigreeData {
  /** マイクロチップ番号（マイクロチップ証明書から） */
  microchipNumber?: string;
  /** マイクロチップ登録日（マイクロチップ証明書から） */
  microchipRegistrationDate?: string;
  /** マイクロチップ暗証記号（マイクロチップ証明書から） */
  microchipPin?: string;
  /** 遺伝子検査結果（遺伝子検査結果から） */
  geneticTestResults?: Array<{
    testName: string;
    result: GeneticTestResultType;
  }>;
  /** 遺伝子検査日（遺伝子検査結果から） */
  geneticTestDate?: string;
  /** 検査機関（遺伝子検査結果から） */
  geneticTestLaboratory?: string;
  /** どうぶつ名（遺伝子検査結果から） */
  animalName?: string;
}

/**
 * PDF解析結果
 * 解析結果のdocumentTypeは常に具体的なタイプ（auto以外）を返す
 */
export interface PDFAnalysisResult {
  /** 解析成功フラグ */
  success: boolean;
  /** ドキュメントタイプ（auto以外の具体的なタイプ） */
  documentType: SpecificDocumentType;
  /** 抽出データ */
  extractedData: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData | ExtractedUnifiedData;
  /** 信頼度スコア（0-100） */
  confidence: number;
  /** 警告メッセージ */
  warnings?: string[];
  /** エラーメッセージ */
  error?: string;
}

/**
 * PDF解析リクエスト
 */
export interface PDFAnalysisRequest {
  /** ファイル */
  file: File;
  /** ドキュメントタイプ */
  documentType: DocumentType;
  /** ユーザーID */
  userId: string;
}

/**
 * ドキュメントタイプのラベル
 */
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  auto: '自動検出（推奨）',
  pedigree: '血統書',
  microchip: 'マイクロチップ証明書',
  geneticTest: '遺伝子検査結果',
} as const;

/**
 * 遺伝子検査結果のラベル
 */
export const GENETIC_TEST_RESULT_LABELS: Record<GeneticTestResultType, string> = {
  clear: 'クリア',
  carrier: 'キャリア',
  affected: 'アフェクテッド',
  unknown: '不明',
} as const;

/**
 * 遺伝子検査結果の色
 */
export const GENETIC_TEST_RESULT_COLORS: Record<GeneticTestResultType, string> = {
  clear: 'green',
  carrier: 'yellow',
  affected: 'red',
  unknown: 'gray',
} as const;
